import { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Plus, Send, Trash2, Users, Layers, Search, CheckSquare, Square } from "lucide-react";
import { toast } from "sonner";

export default function Assignments() {
  const [items, setItems] = useState([]);
  const [quizzes, setQuizzes] = useState([]);
  const [masters, setMasters] = useState({ depts: [], subs: [], cats: [] });
  const [quizTypes, setQuizTypes] = useState([]);

  // single-assignment dialog
  const [open, setOpen] = useState(false);
  const emptyForm = { questionnaire_id: "", department_id: "", sub_department_id: "", category_id: "", employee_ids: [] };
  const [form, setForm] = useState(emptyForm);
  const [matches, setMatches] = useState([]);
  const [matchSearch, setMatchSearch] = useState("");
  const [matchLoading, setMatchLoading] = useState(false);
  const [mode, setMode] = useState("all"); // "all" | "specific"

  // bulk-by-type dialog
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkForm, setBulkForm] = useState({ quiz_type_id: "", department_ids: [] });
  const [viewMode, setViewMode] = useState("cards"); // "cards" | "list"
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  async function load() {
    const [a, q, d, s, c, qt] = await Promise.all([
      api.get("/assignments"),
      api.get("/questionnaires"),
      api.get("/masters/departments"),
      api.get("/masters/sub-departments"),
      api.get("/masters/categories"),
      api.get("/masters/quiz-types"),
    ]);
    setItems(a.data); setQuizzes(q.data);
    setMasters({ depts: d.data, subs: s.data, cats: c.data });
    setQuizTypes(qt.data || []);
  }
  useEffect(() => { load(); }, []);

  // Live-preview matching employees when filters change
  useEffect(() => {
    if (!open) return;
    const fetchMatches = async () => {
      setMatchLoading(true);
      try {
        const params = { page: 1, limit: 200, status: "Active" };
        if (matchSearch) params.search = matchSearch;
        if (form.department_id) params.department_id = form.department_id;
        const r = await api.get("/employees", { params });
        let list = r.data?.data || [];
        // Client-side narrow by sub-dept / category since the list endpoint
        // only supports department_id server-side
        if (form.sub_department_id) list = list.filter((e) => e.sub_department_id === form.sub_department_id);
        if (form.category_id) list = list.filter((e) => e.category_id === form.category_id);
        setMatches(list);
      } finally { setMatchLoading(false); }
    };
    fetchMatches();
  }, [open, form.department_id, form.sub_department_id, form.category_id, matchSearch]);

  function toggleEmp(id) {
    const cur = new Set(form.employee_ids);
    if (cur.has(id)) cur.delete(id); else cur.add(id);
    setForm({ ...form, employee_ids: Array.from(cur) });
  }
  function selectAllVisible() {
    const ids = matches.map((e) => e.id);
    setForm({ ...form, employee_ids: ids });
  }
  function clearSelection() {
    setForm({ ...form, employee_ids: [] });
  }

  async function create() {
    if (!form.questionnaire_id) { toast.error("Select a quiz"); return; }
    const payload = { ...form };
    if (mode === "all") payload.employee_ids = [];
    Object.keys(payload).forEach(k => { if (payload[k] === "" || payload[k] === null) delete payload[k]; });
    try {
      const r = await api.post("/assignments", payload);
      toast.success(`Assigned to ${r.data.target_count} employees`);
      setOpen(false); setForm(emptyForm); setMode("all"); setMatchSearch("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }

  async function runBulkByType() {
    if (!bulkForm.quiz_type_id) { toast.error("Pick a quiz type"); return; }
    if (!bulkForm.department_ids.length) { toast.error("Pick at least one department"); return; }
    try {
      const r = await api.post("/assignments/bulk-by-type", bulkForm);
      toast.success(`Created ${r.data.assignments_created} assignments · ${r.data.total_targets} targets`);
      setBulkOpen(false); setBulkForm({ quiz_type_id: "", department_ids: [] });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  }

  async function remove(id) {
    if (!confirm("Delete this assignment?")) return;
    await api.delete(`/assignments/${id}`); load();
  }
  async function shareAll(qid, channel) {
    await api.post("/assignments/share", { questionnaire_id: qid, channel, employee_ids: [] });
    toast.success(`Shared via ${channel} (simulated)`);
  }

  const findName = (arr, id) => arr.find(x => x.id === id)?.name || null;
  const visibleMatches = useMemo(() => matches, [matches]);
  const allChecked = visibleMatches.length > 0 && visibleMatches.every((e) => form.employee_ids.includes(e.id));

  // Auto-fill bulk dept selection from quiz_type defaults
  useEffect(() => {
    if (!bulkForm.quiz_type_id) return;
    const t = quizTypes.find((x) => x.id === bulkForm.quiz_type_id);
    if (t?.default_department_ids?.length) {
      setBulkForm((f) => ({ ...f, department_ids: t.default_department_ids }));
    }
  }, [bulkForm.quiz_type_id, quizTypes]);

  return (
    <div className="p-6 lg:p-10 space-y-6">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-sky-600 font-bold">Distribute</div>
          <h1 className="font-display font-black text-3xl lg:text-4xl text-slate-900 mt-1">Assignments</h1>
          <p className="text-slate-500 mt-1">Assign quizzes to Departments, Sub-Departments, Categories — or specific employees.</p>
        </div>
        <div className="flex items-center gap-2">
          <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="rounded-xl" data-testid="bulk-type-btn">
                <Layers className="h-4 w-4 mr-1.5"/>Bulk by Quiz Type
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle>Bulk Assign by Quiz Type</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <p className="text-sm text-slate-500">Assigns <span className="font-bold">every quiz</span> tagged with the chosen type to <span className="font-bold">every employee</span> in the chosen department(s).</p>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500">Quiz Type *</Label>
                  <Select value={bulkForm.quiz_type_id || undefined} onValueChange={(v)=>setBulkForm({ ...bulkForm, quiz_type_id: v })}>
                    <SelectTrigger data-testid="bulk-type-select"><SelectValue placeholder="Select…"/></SelectTrigger>
                    <SelectContent>
                      {quizTypes.length === 0 && <SelectItem value="empty" disabled>No quiz types — add in Masters</SelectItem>}
                      {quizTypes.map((t) => (
                        <SelectItem key={t.id} value={t.id}>
                          <span className="inline-flex items-center gap-2">
                            <span className="h-2.5 w-2.5 rounded-full" style={{ background: t.color }}/>{t.name}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500">Departments *</Label>
                  <div className="flex flex-wrap gap-1.5 pt-1">
                    {masters.depts.map((d) => {
                      const sel = bulkForm.department_ids.includes(d.id);
                      return (
                        <button key={d.id} type="button" onClick={() => {
                          const cur = new Set(bulkForm.department_ids);
                          if (cur.has(d.id)) cur.delete(d.id); else cur.add(d.id);
                          setBulkForm({ ...bulkForm, department_ids: Array.from(cur) });
                        }} className={`px-3 py-1.5 rounded-full text-xs font-bold border transition ${sel ? "border-sky-500 bg-sky-50 text-sky-700" : "border-slate-200 hover:bg-slate-50 text-slate-600"}`}
                          data-testid={`bulk-dept-${d.id}`}>{d.name}</button>
                      );
                    })}
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={()=>setBulkOpen(false)}>Cancel</Button>
                <Button onClick={runBulkByType} className="bg-sky-500 hover:bg-sky-600" data-testid="bulk-run-btn">Assign</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Dialog open={open} onOpenChange={(o)=>{ setOpen(o); if (!o) { setForm(emptyForm); setMode("all"); setMatchSearch(""); }}}>
            <DialogTrigger asChild>
              <Button className="bg-sky-500 hover:bg-sky-600 rounded-xl" data-testid="new-assignment-btn"><Plus className="h-4 w-4 mr-1.5"/>New Assignment</Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl">
              <DialogHeader><DialogTitle>Create Assignment</DialogTitle></DialogHeader>
              <div className="space-y-4 max-h-[75vh] overflow-y-auto pr-1">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500">Quiz *</Label>
                  <Select value={form.questionnaire_id || undefined} onValueChange={v=>setForm({...form, questionnaire_id: v})}>
                    <SelectTrigger data-testid="assign-quiz-select"><SelectValue placeholder="Select quiz…"/></SelectTrigger>
                    <SelectContent>{quizzes.map(q => <SelectItem key={q.id} value={q.id}>{q.title}</SelectItem>)}</SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <Label className="text-xs text-slate-500">Department</Label>
                    <Select value={form.department_id || "any"} onValueChange={v=>setForm({...form, department_id:v === "any" ? "" : v, sub_department_id: ""})}>
                      <SelectTrigger><SelectValue placeholder="Any"/></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="any">Any</SelectItem>
                        {masters.depts.map(d=> <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Sub-Dept</Label>
                    <Select value={form.sub_department_id || "any"} onValueChange={v=>setForm({...form, sub_department_id: v === "any" ? "" : v})}>
                      <SelectTrigger><SelectValue placeholder="Any"/></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="any">Any</SelectItem>
                        {masters.subs.filter(s=>!form.department_id||s.parent_id===form.department_id).map(d=> <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Category</Label>
                    <Select value={form.category_id || "any"} onValueChange={v=>setForm({...form, category_id: v === "any" ? "" : v})}>
                      <SelectTrigger><SelectValue placeholder="Any"/></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="any">Any</SelectItem>
                        {masters.cats.map(d=> <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Mode toggle */}
                <div className="flex items-center gap-2 pt-1">
                  <button onClick={()=>setMode("all")} data-testid="mode-all"
                    className={`px-3 py-1.5 text-xs font-bold rounded-full border transition ${mode === "all" ? "border-sky-500 bg-sky-50 text-sky-700" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
                    Assign all matching
                  </button>
                  <button onClick={()=>setMode("specific")} data-testid="mode-specific"
                    className={`px-3 py-1.5 text-xs font-bold rounded-full border transition ${mode === "specific" ? "border-sky-500 bg-sky-50 text-sky-700" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
                    Pick specific employees
                  </button>
                  <Badge variant="secondary" className="ml-auto bg-slate-100 text-slate-600" data-testid="match-count">
                    {matchLoading ? "…" : `${matches.length} match${matches.length === 1 ? "" : "es"}`}
                  </Badge>
                </div>

                <div className="border border-slate-200 rounded-xl">
                  <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-100">
                    <Search className="h-4 w-4 text-slate-400"/>
                    <Input placeholder="Search by name / Emp ID / email" value={matchSearch} onChange={(e)=>setMatchSearch(e.target.value)}
                      data-testid="match-search" className="border-0 focus-visible:ring-0 shadow-none px-0 h-7"/>
                    {mode === "specific" && (
                      <>
                        <Button variant="ghost" size="sm" onClick={allChecked ? clearSelection : selectAllVisible} className="h-7 px-2 text-xs" data-testid="select-all-btn">
                          {allChecked ? <Square className="h-3.5 w-3.5 mr-1"/> : <CheckSquare className="h-3.5 w-3.5 mr-1"/>}
                          {allChecked ? "Clear" : "Select all"}
                        </Button>
                        <Badge variant="secondary" className="bg-sky-50 text-sky-700">{form.employee_ids.length} selected</Badge>
                      </>
                    )}
                  </div>
                  <div className="max-h-[300px] overflow-y-auto divide-y divide-slate-100">
                    {visibleMatches.length === 0 && (
                      <div className="p-6 text-center text-sm text-slate-400">No employees match the current filters.</div>
                    )}
                    {visibleMatches.map((e) => {
                      const checked = form.employee_ids.includes(e.id);
                      return (
                        <label key={e.id} className="flex items-center gap-3 px-3 py-2 hover:bg-slate-50 cursor-pointer" data-testid={`match-row-${e.emp_id}`}>
                          {mode === "specific" && (
                            <input type="checkbox" checked={checked} onChange={()=>toggleEmp(e.id)} className="h-4 w-4 accent-sky-600" data-testid={`match-check-${e.emp_id}`}/>
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-bold text-slate-900 truncate">{e.name}</div>
                            <div className="text-[11px] text-slate-500 truncate">{e.emp_id} · {e.email || "no email"}</div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>
                <p className="text-xs text-slate-400">Leave filters empty in "Assign all matching" mode to skip them. In "Pick specific employees" only the checked rows are assigned.</p>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={()=>setOpen(false)}>Cancel</Button>
                <Button onClick={create} className="bg-sky-500 hover:bg-sky-600" data-testid="assign-save-btn">Assign</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </header>

      <div className="flex items-center gap-2">
        <div className="ml-auto text-xs text-slate-500 font-bold uppercase tracking-wider">View:</div>
        <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden">
          <button onClick={()=>setViewMode("cards")} className={`px-3 py-1.5 text-xs font-bold ${viewMode==="cards"?"bg-sky-500 text-white":"bg-white hover:bg-slate-50 text-slate-600"}`} data-testid="view-cards">Cards</button>
          <button onClick={()=>setViewMode("list")} className={`px-3 py-1.5 text-xs font-bold ${viewMode==="list"?"bg-sky-500 text-white":"bg-white hover:bg-slate-50 text-slate-600"}`} data-testid="view-list">List</button>
        </div>
      </div>

      {viewMode === "list" ? (
        <AssignmentsListView items={items} masters={masters} onRemove={remove} onShare={shareAll}
          page={page} setPage={setPage} pageSize={pageSize} setPageSize={setPageSize}/>
      ) : (
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {items.length === 0 && (
          <Card className="md:col-span-2 lg:col-span-3 border-slate-200 rounded-2xl">
            <CardContent className="p-10 text-center text-slate-500">No assignments yet.</CardContent>
          </Card>
        )}
        {items.map(a => (
          <Card key={a.id} className="border-slate-200 rounded-2xl">
            <CardContent className="p-5 space-y-3">
              <div className="flex items-start justify-between">
                <div className="min-w-0">
                  <div className="text-xs uppercase tracking-wider text-sky-600 font-bold">Assignment</div>
                  <h3 className="font-display font-bold text-lg mt-1 truncate">{a.questionnaire?.title}</h3>
                </div>
                <Button variant="ghost" size="sm" onClick={()=>remove(a.id)}><Trash2 className="h-4 w-4 text-red-500"/></Button>
              </div>
              <div className="space-y-1 text-sm">
                {findName(masters.depts, a.department_id) && <div><span className="text-slate-400">Dept:</span> <span className="font-medium">{findName(masters.depts, a.department_id)}</span></div>}
                {findName(masters.subs, a.sub_department_id) && <div><span className="text-slate-400">Sub:</span> <span className="font-medium">{findName(masters.subs, a.sub_department_id)}</span></div>}
                {findName(masters.cats, a.category_id) && <div><span className="text-slate-400">Category:</span> <span className="font-medium">{findName(masters.cats, a.category_id)}</span></div>}
                {(a.employee_ids || []).length > 0 && (
                  <div><span className="text-slate-400">Employees:</span> <span className="font-medium">{a.employee_ids.length} hand-picked</span></div>
                )}
                {!a.department_id && !a.sub_department_id && !a.category_id && (a.employee_ids||[]).length===0 && (
                  <div className="text-slate-400 italic">No filter — empty assignment</div>
                )}
              </div>
              <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
                <div className="flex items-center gap-1.5 text-sm font-bold text-slate-700"><Users className="h-4 w-4"/>{a.target_count} targets</div>
                <div className="ml-auto flex gap-1">
                  <Button size="sm" variant="outline" onClick={()=>shareAll(a.questionnaire_id, "whatsapp")} className="text-green-700 border-green-300">
                    <Send className="h-3.5 w-3.5 mr-1"/>WA
                  </Button>
                  <Button size="sm" variant="outline" onClick={()=>shareAll(a.questionnaire_id, "email")} className="text-sky-700 border-sky-300">
                    <Send className="h-3.5 w-3.5 mr-1"/>Email
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      )}
    </div>
  );
}

function AssignmentsListView({ items, masters, onRemove, onShare, page, setPage, pageSize, setPageSize }) {
  const totalPages = Math.max(1, Math.ceil(items.length / pageSize));
  const slice = items.slice((page-1)*pageSize, page*pageSize);
  const findName = (arr, id) => arr.find(x => x.id === id)?.name || "";
  return (
    <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="assignments-list-table">
          <thead className="text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50">
            <tr>
              <th className="py-3 px-4">Quiz / Training</th>
              <th className="py-3 px-4">Scope</th>
              <th className="py-3 px-4 text-center">Targets</th>
              <th className="py-3 px-4">Created</th>
              <th className="py-3 px-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan="5" className="py-10 text-center text-slate-400">No assignments yet.</td></tr>}
            {slice.map(a => {
              const scope = [];
              if (a.department_id) scope.push(`Dept: ${findName(masters.depts, a.department_id)}`);
              if (a.sub_department_id) scope.push(`Sub: ${findName(masters.subs, a.sub_department_id)}`);
              if (a.category_id) scope.push(`Cat: ${findName(masters.cats, a.category_id)}`);
              if ((a.employee_ids || []).length) scope.push(`${a.employee_ids.length} hand-picked`);
              return (
                <tr key={a.id} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`assignment-row-${a.id}`}>
                  <td className="py-3 px-4">
                    <div className="font-medium">{a.questionnaire?.title || "—"}</div>
                    <div className="text-xs text-slate-400">{a.questionnaire?.topic || ""}</div>
                  </td>
                  <td className="py-3 px-4 text-xs text-slate-600">{scope.join(" · ") || <span className="text-slate-300 italic">No filter</span>}</td>
                  <td className="py-3 px-4 text-center font-bold">{a.target_count}</td>
                  <td className="py-3 px-4 text-xs text-slate-500">{a.created_at ? new Date(a.created_at).toLocaleDateString() : "—"}</td>
                  <td className="py-3 px-4 text-right">
                    <div className="inline-flex gap-1.5">
                      <Button size="sm" variant="outline" onClick={()=>onShare(a.questionnaire_id, "whatsapp")} className="text-green-700 border-green-300 h-8"><Send className="h-3 w-3 mr-1"/>WA</Button>
                      <Button size="sm" variant="outline" onClick={()=>onShare(a.questionnaire_id, "email")} className="text-sky-700 border-sky-300 h-8"><Send className="h-3 w-3 mr-1"/>Email</Button>
                      <Button size="sm" variant="ghost" onClick={()=>onRemove(a.id)} className="h-8"><Trash2 className="h-3.5 w-3.5 text-red-500"/></Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 text-xs text-slate-500">
        <div>Showing {items.length ? (page-1)*pageSize + 1 : 0}–{Math.min(items.length, page*pageSize)} of {items.length}</div>
        <div className="flex items-center gap-2">
          <select value={pageSize} onChange={e=>{ setPageSize(parseInt(e.target.value)); setPage(1); }} className="border border-slate-200 rounded px-2 py-1" data-testid="list-page-size">
            <option value="10">10</option><option value="25">25</option><option value="50">50</option><option value="100">100</option>
          </select>
          <Button variant="outline" size="sm" disabled={page<=1} onClick={()=>setPage(p=>p-1)} className="h-7" data-testid="list-prev">Prev</Button>
          <span className="font-mono">{page} / {totalPages}</span>
          <Button variant="outline" size="sm" disabled={page>=totalPages} onClick={()=>setPage(p=>p+1)} className="h-7" data-testid="list-next">Next</Button>
        </div>
      </div>
    </div>
  );
}
